import React, { useState, useEffect } from "react";
import { Box, SelectChangeEvent } from "@mui/material";
import { Grid } from "@material-ui/core";
import CustomModal from "../../components/common/customModal";
import CustomInput from "../../components/common/inputField";

import AddSearchDropdown from "../../components/addAndSeachSelect";
import {
  openRoadFormData,
  openRoadFormErrors,
  openRoadState,
} from "../../types/dashboard";
import { showToast } from "../../components/common/toastNotification";
import dashboardService from "../../services/DashboardServices";
import VideoFileUpload from "../../components/common/videoFileUpload";

interface OpenRoadModalProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  openRoadUpdateData: openRoadState;
  openRoadTypesList: { id: number; type: string }[];
}

const OpenRoad: React.FC<OpenRoadModalProps> = ({
  open,
  onClose,
  onSave,
  openRoadUpdateData,
  openRoadTypesList,
}) => {
  const [typeData, setTypeData] = useState<
    { label: string; value: number | string }[]
  >([]);
  const [formData, setFormData] = useState<openRoadFormData>({
    id: null,
    videoFile: null,
    title: "",
    type: "",
    youtubeUrl: "",
    thumbNailPath: "",
    isYouTubeLink: false,
  });

  const [formErrors, setFormErrors] = useState({
    title: "",
    type: "",
    youtubeUrl: "",
  });
  const [btnLoading, setBtnLoading] = useState(false);
  const [isVideoUpdate, setIsVideoUpdate] = useState("");

  useEffect(() => {
    if (openRoadUpdateData.title !== "") {
      setFormData({
        id: openRoadUpdateData.id || null,
        title: openRoadUpdateData.title || "",
        videoFile: openRoadUpdateData.filePath || "",
        youtubeUrl: openRoadUpdateData.youtubeUrl || "",
        thumbNailPath: openRoadUpdateData.thumbNailPath || "",
        type: openRoadUpdateData.type || "",
        isYouTubeLink: openRoadUpdateData.isYouTubeLink || false,
      });

      setIsVideoUpdate(
        openRoadUpdateData.isYouTubeLink
          ? openRoadUpdateData.youtubeUrl ?? ""
          : openRoadUpdateData.filePath ?? ""
      );
    } else {
      setFormData({
        id: null,
        videoFile: null,
        title: "",
        type: "",
        youtubeUrl: "",
        thumbNailPath: "",
        isYouTubeLink: false,
      });
      setTypeData([]);
    }
    const transformedList = openRoadTypesList.map((item) => ({
      value: item.type,
      label: item.type,
    }));

    setTypeData(transformedList);
  }, [openRoadUpdateData, open, openRoadTypesList]);

  //// handle Input Onchange code
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    if (name === "youtubeUrl") {
      setIsVideoUpdate("");
    }
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setFormErrors((prevErrors) => ({
      ...prevErrors,
      [name]: "",
    }));
  };

  const handleTypeChange = (event: SelectChangeEvent<string | number>) => {
    const { value } = event.target;

    setFormData((prev) => ({
      ...prev,
      ["type"]: value,
      isYouTubeLink: prev.youtubeUrl ? true : prev.isYouTubeLink,
    }));

    setFormErrors((prevErrors) => ({
      ...prevErrors,
      ["type"]: "",
    }));
  };
  /// handle File upload code
  const handleFileSelect = (file: File | null) => {
    setFormData((prev) => ({ ...prev, videoFile: file, isYouTubeLink: false }));
    setIsVideoUpdate("");
  };
  //// handle get Type list
  const handleTypeUpdate = (
    updatedList: { label: string; value: string | number }[]
  ) => {
    setTypeData(updatedList);
  };

  //// handle form validation code
  const validateForm = () => {
    const errors: openRoadFormErrors = {};

    if (!formData.title.trim()) {
      errors.title = "Title is required.";
    }

    if (!formData.type) {
      errors.type = "Type is required.";
    }
    const youtubeUrl = formData.youtubeUrl?.trim() || "";
    if (!youtubeUrl && !formData.videoFile) {
      errors.youtubeUrl = "Either a video file or a URL must be provided.";
    } else if (
      !/^(https?:\/\/)?([a-z0-9-]+\.)+[a-z0-9]{2,6}(:\d+)?(\/[^\s]*)?$/i.test(
        youtubeUrl
      )
    ) {
      errors.youtubeUrl = "Enter a valid  URL.";
    }

    if (errors.youtubeUrl) {
      showToast(errors.youtubeUrl, "error");
    }

    setFormErrors({
      title: errors.title || "",
      type: errors.type || "",
      youtubeUrl: errors.youtubeUrl || "",
    });

    return Object.keys(errors).length === 0;
  };

  const handleSaveData = async () => {
    if (!validateForm()) return;
    try {
      const { id, videoFile, title, type, youtubeUrl, thumbNailPath } =
        formData;

      setBtnLoading(true);

      const isYouTube =
        youtubeUrl?.trim() !== "" && !(videoFile instanceof File);

      const formDataToSend = new FormData();
      formDataToSend.append("Id", id?.toString() || "0");
      formDataToSend.append("Title", title.trim());
      formDataToSend.append("Type", String(type));
      formDataToSend.append("IsYouTubeLink", isYouTube ? "true" : "false");
      formDataToSend.append(
        "typeList",
        typeData.map((item) => item.value).join(", ")
      );

      if (id) {
        if (isVideoUpdate) {
          formDataToSend.append(
            "URL",
            isYouTube ? youtubeUrl ?? "" : videoFile ?? ""
          );
          formDataToSend.append("Thumbnail", thumbNailPath || "");
        }

        if (videoFile instanceof File) {
          formDataToSend.append("file", videoFile);
          formDataToSend.append("URL", "");
        } else if (youtubeUrl?.trim()) {
          formDataToSend.append("URL", youtubeUrl.trim());
          formDataToSend.append("file", "");
        }
      } else {
        if (videoFile instanceof File) {
          formDataToSend.append("file", videoFile);
          formDataToSend.append("URL", "");
        } else if (youtubeUrl?.trim()) {
          formDataToSend.append("URL", youtubeUrl.trim());
          formDataToSend.append("file", "");
        }
      }

      const apiCall = id
        ? dashboardService.updateOpenRoadDataAPI
        : dashboardService.addOpenRoadDataAPI;

      await apiCall(formDataToSend)
        .then((response) => {
          if (response && response.data) {
            showToast(response.data.message, "success");
            setFormData({
              id: null,
              videoFile: null,
              title: "",
              type: "",
              youtubeUrl: "",
              thumbNailPath: "",
              isYouTubeLink: false,
            });
            onSave();
            onClose();
          } else {
            showToast(response?.data?.message, "error");
          }
        })
        .catch((error) => {
          console.error("Error saving Open Road data:", error);
        })
        .finally(() => {
          setBtnLoading(false);
        });
    } catch (error) {
      console.error("Error saving Open Road data:", error);
    }
  };

  return (
    <CustomModal
      open={open}
      handleClose={onClose}
      title="Open Road (DEI)"
      onSave={handleSaveData}
      width={800}
      btnLoading={btnLoading}
    >
      <Box mt={2}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={12}>
            <VideoFileUpload
              onFileSelect={handleFileSelect}
              isYouTubeLink={formData.isYouTubeLink}
              videoFilePath={
                formData.videoFile && !formData.isYouTubeLink
                  ? (formData.videoFile as string)
                  : ""
              }
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <CustomInput
              label="Title"
              type="text"
              name="title"
              placeholder="Enter Title"
              value={formData.title}
              onChange={handleChange}
              error={formErrors.title}
              required
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <AddSearchDropdown
              options={typeData.length > 0 ? typeData : []}
              label="Type"
              value={formData.type}
              placeHolder="Select type"
              onChange={handleTypeChange}
              error={formErrors.type}
              required
              onUpdateTypeList={handleTypeUpdate}
            />
          </Grid>
          <Grid item xs={12} md={12}>
            <CustomInput
              label="Upload video using a URL."
              type="text"
              name="youtubeUrl"
              placeholder="Enter the URL"
              value={formData.youtubeUrl}
              onChange={handleChange}
              error={formErrors.youtubeUrl}
              required
            />
          </Grid>
        </Grid>
      </Box>
    </CustomModal>
  );
};

export default OpenRoad;
